import { useState, useCallback, useRef } from 'react';
import type { LeadSubmitResponse } from '@highereducation/delivery-system';
import type Lead from '../types/leadDelivery';
import type { Question, Step } from '../types/types';
import {
  initConversion,
  submitLead,
} from '../utils/data-fetch/postLeadSubmission';
import { getFlow } from '../services/api/flowService';
import filterPostResultsQuestions from '../utils/filterPostResultsQuestions';
import { DeliverySystemApiClient } from '@highereducation/delivery-system';
import { createServiceTrackedHeaders } from '../utils/createServiceTrackedHeaders';
import { uuid } from '../shared/utils';
import { getApiConfig } from '../services/api/utils';
import { useDynamicExperienceContext } from '../providers/DynamicExperienceProvider';
import useFlowTrackingEvents from './useFlowTrackingEvents';
import type { EventContext } from '../utils/createEventingPayloadsDE';
import { isPii } from '../utils/isPii';
import { resolveApplicationCtaHref } from '../utils/resolveApplicationCtaHref';
import { mergePartnerIdentifiers } from '../utils/mergePartnerIdentifiers';
import { ctaUrlForTelemetry } from '../utils/sanitizeOutboundUrlForTelemetry';
import { useSiteConfig } from '../providers/ExperienceConfigProvider';
import type { LeadSubmissionConfig } from '../types/leadSubmission';
import type { BuildLeadObjectInputs } from '../utils/buildLeadObject';
import type { Result } from '../types/result';
import { buildConversionCreationRequest } from '../utils/buildConversionCreationRequest';
import { resolveLeadComplianceTokens } from './leadComplianceTokens';

const DEFAULT_TENANT_ID = 'default';
const PRQ_FLOW_ID = 'prq-flow';

// PII fields collected on the conversion-drawer confirmation step. Per the
// eventing spec (§9.7), `FormContinued` and `FormSubmitted` must include all
// fields collected up to that point; both carry these sanitized PII entries
// (FormSubmitted additionally appends the answered PRQ fields).
const PII_LEAD_FIELDS: ReadonlyArray<{
  fieldName: string;
  fieldType: string;
  fieldValue: string;
}> = [
  { fieldName: 'first-name', fieldType: 'text', fieldValue: 'SANITIZED' },
  { fieldName: 'last-name', fieldType: 'text', fieldValue: 'SANITIZED' },
  { fieldName: 'email', fieldType: 'text', fieldValue: 'SANITIZED' },
  { fieldName: 'phone', fieldType: 'text', fieldValue: 'SANITIZED' },
];

type UseLeadSubmissionWithQuestionsParams = {
  lead: Lead;
  activeProspectToken: string;
  jornayaToken: string;
  programId: string;
  selectedResult: Result | undefined;
  productClickedCorrelationId: string;
  contactId?: string;
  inputs: BuildLeadObjectInputs;
  record: LeadSubmissionConfig['record'] | undefined;
  applicationCtaURL?: string;
  needsActiveProspectToken?: boolean;
  /**
   * DSL result-step config id (`dslConfig.configId`). Supplied only by
   * the governed DSL lead flow (`LeadFlowProvider`). When present, it gates
   * the DSL-only `FormContinued` emission on the PII → PRQ transition. The
   * form events themselves carry no top-level `customDimensions` (the
   * registered Tagular form schemas reject it with HTTP 400); the funnel
   * correlates to the config via the product/element `dslConfigId`
   * dimensions and the shared `correlationId`.
   */
  dslConfigId?: string;
  flowType?: string;
  trafficSource?: string;
  monetizationChannel?: string;
  /**
   * Conversion id created at the application-view-routing click (pre-submit)
   * by the governed lead flow (`LeadFlowProvider.openGovernedFlow`). When
   * present, submit **reuses/completes** this conversion instead of creating a
   * second one, keeping the `APP_VIEWED` (create) and submit signals on a
   * single conversion — correlation continuity via
   * `productClickedCorrelationId`. Absent on the imperative `ResultsPage`
   * submit path, which keeps its create-at-submit behavior.
   */
  precreatedConversionId?: string;
  /**
   * Reads the current open's click-time conversion, including one still in
   * flight. The direct-submit path calls this once, before the post-result
   * question lookup, and keeps that promise for the lead it already captured.
   * A newer open during the lookup cannot replace it. A non-empty id skips
   * `initConversion`; `undefined` keeps the single create-at-submit fallback.
   */
  resolvePrecreatedConversionId?: () => Promise<string | undefined> | undefined;
  /**
   * Fired with the raw LDS v4 submit response right after a successful
   * `submitLead` call (both the direct-submit and additional-questions
   * paths). The second argument is the result this submission used.
   * Callers should ignore the response when that result is no longer
   * selected, so a late submit cannot write its identifiers onto a newer
   * program. Lets the caller stash `referenceId`/`partnerIdentifiers` into
   * its own generic lead-flow state for click-time CTA interpolation
   * (EDUN-14483) without this hook owning that state itself.
   */
  onLeadSubmitted?: (
    response: LeadSubmitResponse,
    submittedResult: Result,
  ) => void;
};

type UseLeadSubmissionWithQuestionsReturn = {
  isSubmitting: boolean;
  showAdditionalQuestions: boolean;
  additionalQuestions: Question[];
  isLoadingQuestions: boolean;
  questionsError: Error | null;
  checkForAdditionalQuestionsAndSubmit: () => Promise<{
    hasQuestions: boolean;
  }>;
  submitWithAdditionalData: (additionalData: any) => Promise<void>;
  resetAdditionalQuestions: () => void;
};

/**
 * Hook to handle lead submission with potential additional questions
 *
 * This hook manages the flow of:
 * 1. Fetching LDS v4 diffy to determine missing required fields
 * 2. If all required fields are present, submitting the lead immediately
 * 3. If fields are missing, providing questions to the parent component
 * 4. Parent component renders PostResultFormContainer with the questions
 * 5. After user fills the form, submitWithAdditionalData merges and submits
 */
export const useLeadSubmissionWithQuestions = ({
  lead,
  needsActiveProspectToken,
  activeProspectToken,
  jornayaToken,
  programId,
  selectedResult,
  productClickedCorrelationId,
  contactId,
  inputs,
  record,
  dslConfigId,
  flowType,
  trafficSource,
  monetizationChannel,
  precreatedConversionId,
  resolvePrecreatedConversionId,
  onLeadSubmitted,
}: UseLeadSubmissionWithQuestionsParams): UseLeadSubmissionWithQuestionsReturn => {
  // Written every render and read inside `performLeadSubmission` so a submit
  // callback created while the click-time id was still empty sees the current
  // selection, including an in-flight create.
  const jornayaRequiredRef = useRef<boolean | null>(null);
  const precreatedConversionIdRef = useRef(precreatedConversionId);
  const resolvePrecreatedConversionIdRef = useRef(
    resolvePrecreatedConversionId,
  );
  precreatedConversionIdRef.current = precreatedConversionId;
  resolvePrecreatedConversionIdRef.current = resolvePrecreatedConversionId;
  // The program this hook is currently rendering. In-flight question lookups
  // and submits capture it before they await, then skip shared question state
  // when a newer program is selected.
  const selectedResultRef = useRef(selectedResult);
  selectedResultRef.current = selectedResult;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAdditionalQuestions, setShowAdditionalQuestions] = useState(false);
  const [additionalQuestions, setAdditionalQuestions] = useState<Question[]>(
    [],
  );
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [questionsError, setQuestionsError] = useState<Error | null>(null);
  const { eventContext } = useDynamicExperienceContext();
  const { triggerFormEvents } = useFlowTrackingEvents();
  const siteConfig = useSiteConfig();

  /**
   *  Gets PRQs based on the program and school
   */
  const fetchPostResultQuestions = async (): Promise<Question[]> => {
    const requestResult = selectedResultRef.current;
    setIsLoadingQuestions(true);
    setQuestionsError(null);

    try {
      const config = getApiConfig();

      const organizationId = selectedResult?.organizationId?.trim();
      const offeringUuid = selectedResult?.offeringUuid?.trim();
      if (!organizationId) {
        throw new Error(
          'LDS v4 organizationId is required to fetch post-result questions',
        );
      }

      const diffyHeaders = createServiceTrackedHeaders(uuid());
      const deliverySystemClient = new DeliverySystemApiClient({
        baseUrl: config.deliverySystemApiUrl || '',
        defaultHeaders: diffyHeaders,
      });

      // Fetch the full lead schema first. Unlike diffy (a difference document
      // that only flags which fields are still *missing*), the full schema
      // carries per-field enum constraints — e.g. `properties.programTrack.anyOf`
      // — which the post-results option narrowing in `filterPostResultsQuestions`
      // needs to restrict programTrack to the offering's valid tracks. It is
      // also the source of truth for whether `thirdPartyLeadId` is required,
      // which decides whether submit waits for a Jornaya token.
      const leadSchema = await deliverySystemClient.getLeadSchemaV4({
        organizationId,
        offeringUuid,
      });
      const requiresThirdPartyLeadId =
        leadSchema.required?.includes('thirdPartyLeadId') === true;
      jornayaRequiredRef.current = requiresThirdPartyLeadId;

      const tokens = await resolveLeadComplianceTokens({
        jornayaToken,
        activeProspectToken,
        needsActiveProspectToken: needsActiveProspectToken || false,
        waitForJornaya: requiresThirdPartyLeadId,
        schoolSlug: selectedResult?.schoolSlug,
        organizationId,
      });
      const thirdPartyLeadId = needsActiveProspectToken
        ? tokens.activeProspectToken
        : tokens.jornayaToken;

      // Build the probe payload with the token attached when the schema
      // requires it, so the diffy request reflects the lead we will actually
      // submit; otherwise diffy reports `thirdPartyLeadId` as missing and it
      // leaks into `requiredFields` (and a default PRQ field). Use a copy
      // rather than mutating the caller-provided `lead` — the submit path
      // rebuilds from `inputs` and the BFF sets `thirdPartyLeadId` itself, so
      // nothing downstream depends on mutating this object.
      const leadForDiffy = leadSchema.required?.includes('thirdPartyLeadId')
        ? { ...lead, thirdPartyLeadId }
        : lead;

      // Diffy is the difference document: it tells us which required fields are
      // still missing from the (token-bearing) lead, which drives
      // `requiredFields` below.
      const diffyData = await deliverySystemClient.getDiffyV4(
        { lead: leadForDiffy },
        { organizationId, offeringUuid },
      );

      if (!diffyData?.diffy) {
        throw new Error('LDS v4 diffy response did not include a diffy schema');
      }

      const diffySchema = diffyData.diffy;

      // Resolve program-specific post-result questions from the offering's
      // `programFields` pointer instead of the legacy Lambda `degree-fields`
      // endpoint. Presence of `flowId` is the on/off signal: when set, its
      // dynamic-flows flow (under the `default` tenant) supplies this program's
      // questions; when absent, there are no program-specific questions and we
      // submit with the global PRQs only. `getFlow` never throws — it returns
      // `{ data: null, error }` on failure — so a missing or failed program
      // flow degrades gracefully to "no program questions" rather than blocking
      // lead submission.
      const programFlowId = selectedResult?.programFields?.flowId;
      let programFlowQuestions: Step[] = [];
      if (programFlowId) {
        const programFlowResult = await getFlow(
          DEFAULT_TENANT_ID,
          programFlowId,
        );
        if (programFlowResult.error) {
          console.warn(
            `Failed to fetch program-fields flow "${programFlowId}": ${programFlowResult.error}`,
          );
        }
        programFlowQuestions = (programFlowResult.data ?? []) as Step[];
      }

      // Fetch post results questions from dynamic experience API
      const prqResult = await getFlow(DEFAULT_TENANT_ID, PRQ_FLOW_ID);
      const postResultsQuestions = prqResult.data || [];
      /**
       * Collect all missing required fields from the LDS v4 diffy response.
       *
       * Edge case: The location field is a nested object with required fields.
       * In case of nested required fields, we need to include only the nested fields (the children).
       */
      const topLevelRequired = diffySchema.required || [];
      const requiredFields: string[] = [];
      const parentsWithNestedFields = new Set<string>();

      // Collect nested required fields from properties
      const properties = diffySchema.properties || {};

      Object.entries(properties).forEach(([parentKey, property]) => {
        const nestedRequired = (property as any).required;

        if (Array.isArray(nestedRequired) && nestedRequired.length > 0) {
          parentsWithNestedFields.add(parentKey);

          // Add unique nested fields
          nestedRequired.forEach((field: string) => {
            if (!requiredFields.includes(field)) {
              requiredFields.push(field);
            }
          });
        }
      });

      // Add top-level fields that don't have nested requirements
      topLevelRequired.forEach((field: string) => {
        const hasNestedFields = parentsWithNestedFields.has(field);
        const isAlreadyIncluded = requiredFields.includes(field);

        if (!hasNestedFields && !isAlreadyIncluded) {
          requiredFields.push(field);
        }
      });

      const filteredQuestions = await filterPostResultsQuestions({
        postResultsQuestions: postResultsQuestions as Step[],
        programFlowQuestions,
        requiredFields,
        schema: leadSchema,
      });

      return filteredQuestions as Question[];
    } catch (error) {
      if (selectedResultRef.current === requestResult) {
        setQuestionsError(
          error instanceof Error ? error : new Error('Unknown error'),
        );
      }
      throw error;
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  /**
   * Submits the lead to the delivery service.
   * Initializes a conversion first (mirroring useHandleSubmit) so that the
   * real conversionId propagates as heclid and to completeConversion.
   *
   * `capturedClickConversion` is the click-time promise read when the direct
   * submit started, before the post-result question lookup. The additional-
   * questions path has no lookup before this call, so it omits the capture
   * and this function reads the resolver when it runs.
   */
  const performLeadSubmission = async (
    mergedInputs: BuildLeadObjectInputs,
    capturedClickConversion?: Promise<string | undefined>,
  ) => {
    if (!record) {
      throw new Error('Cannot submit lead: record is not available');
    }
    if (!selectedResult) {
      throw new Error('Cannot submit lead: selectedResult is not available');
    }
    const submittedResult = selectedResult;
    setIsSubmitting(true);
    try {
      // Reuse the conversion created at the application-view-routing click
      // (governed lead flow) when present, so submit completes the same
      // conversion the `APP_VIEWED` create started rather than minting a
      // duplicate. A non-empty id skips `initConversion`. An empty id keeps
      // one create-at-submit (imperative `ResultsPage`, or a failed
      // click-time create).
      let conversionId: string | undefined;
      try {
        if (capturedClickConversion) {
          conversionId = await capturedClickConversion;
        } else {
          const resolveId = resolvePrecreatedConversionIdRef.current;
          conversionId = resolveId
            ? await resolveId()
            : precreatedConversionIdRef.current;
        }
      } catch {
        // A rejected click-time create is the same as no id: one
        // create-at-submit, rather than failing the lead.
        conversionId = undefined;
      }
      if (!conversionId) {
        const conversion = buildConversionCreationRequest(
          submittedResult,
          siteConfig ?? ({} as any),
          submittedResult.productClickedCorrelationId ||
            productClickedCorrelationId,
          {
            flowType,
            trafficSource,
            monetizationChannel,
            location: {
              country: lead.location?.country,
              state: lead.location?.stateAbbr ?? lead.location?.state,
            },
          },
        );
        const conversionResponse = await initConversion(conversion);
        conversionId = conversionResponse.conversionId;
      }

      const tokens = await resolveLeadComplianceTokens({
        jornayaToken,
        activeProspectToken,
        needsActiveProspectToken: needsActiveProspectToken || false,
        waitForJornaya: jornayaRequiredRef.current !== false,
        schoolSlug: selectedResult.schoolSlug,
        organizationId: selectedResult.organizationId?.trim(),
      });
      const response = await submitLead({
        activeProspectToken: tokens.activeProspectToken,
        jornayaToken: tokens.jornayaToken,
        needsActiveProspectToken: needsActiveProspectToken || false,
        record,
        result: submittedResult,
        conversionId,
        inputs: mergedInputs,
        productCorrelationId: productClickedCorrelationId,
        contactId,
        identityToken: siteConfig?.identityToken,
        monetizationChannel,
      });
      onLeadSubmitted?.(response, submittedResult);
      return response;
    } finally {
      setIsSubmitting(false);
    }
  };

  const fireFormSubmittedEvent = useCallback(
    (
      productClickedCorrelationId: string,
      submitResponse?: LeadSubmitResponse,
      prqFormData?: Record<string, any>,
    ) => {
      // Build PRQ fields only from the actual PRQ answers
      const prqFields: Array<{
        fieldName: string;
        fieldType: string;
        fieldValue: string;
      }> = [];

      if (prqFormData && additionalQuestions.length > 0) {
        Object.entries(prqFormData).forEach(([questionId, answer]) => {
          const question = additionalQuestions.find((q) => q.id === questionId);
          if (question) {
            prqFields.push({
              fieldName: question.title || questionId,
              fieldType: question.type || 'text',
              fieldValue: isPii(
                String(question.leadFieldAssociated),
                question as Step,
                question.title,
              )
                ? 'SANITIZED'
                : String(answer ?? ''),
            });
          }
        });
      }

      const interpolatedOutboundUrl = resolveApplicationCtaHref(
        selectedResult,
        {
          lead,
          partnerIdentifiers: submitResponse
            ? mergePartnerIdentifiers(submitResponse)
            : undefined,
        },
      );

      const formSubmittedContext = {
        ...eventContext,
        productClickedCorrelationId,
        outboundUrl:
          ctaUrlForTelemetry(
            interpolatedOutboundUrl,
            selectedResult?.applicationCtaFieldMap,
          ) ?? '',
        formName: 'dexp-hosted-lead-form',
        stepName: 'pii-confirmation',
        stepType: 'LEAD',
        field: [...PII_LEAD_FIELDS, ...prqFields],
      } as EventContext;

      // The registered `redventures.usertracking.v3.FormSubmitted` schema
      // does not define a top-level `customDimensions`; sending one makes
      // the Tagular endpoint reject the lead-submit with HTTP 400. The DSL
      // funnel still correlates to the config via the product/element
      // `dslConfigId` dimensions and the shared `correlationId`, so the
      // FormSubmitted payload stays identical on both paths.
      triggerFormEvents('FormSubmitted', formSubmittedContext);
    },
    [
      eventContext,
      triggerFormEvents,
      additionalQuestions,
      selectedResult,
      lead,
    ],
  );

  /**
   * Fire `FormContinued` at the moment the PII confirmation step completes and
   * the user advances into the PRQ (Additional Questions) step — i.e. when the
   * drawer CTA is clicked and the `AdditionalQuestions` form opens (spec §9.9.1
   * row 9). DSL path only: the imperative result page fires no `FormContinued`
   * for this transition today, so gating on `dslConfigId` keeps it
   * byte-identical (AC #3). Carries the PII fields collected up to this point;
   * the answered PRQ fields ride on the later `FormSubmitted`.
   */
  const fireFormContinuedEvent = useCallback(() => {
    if (!dslConfigId) return;

    const formContinuedContext = {
      ...eventContext,
      productClickedCorrelationId,
      formName: 'dexp-hosted-lead-form',
      stepName: 'pii-confirmation',
      stepType: 'LEAD',
      field: [...PII_LEAD_FIELDS],
    } as EventContext;

    // The registered `redventures.usertracking.v3.FormContinued` schema does
    // not define a top-level `customDimensions`; sending one makes the
    // Tagular endpoint reject the event with HTTP 400. This transition is
    // still DSL-only (gated on `dslConfigId` above); the funnel correlates
    // via the product/element `dslConfigId` dimensions and the shared
    // `correlationId`.
    triggerFormEvents('FormContinued', formContinuedContext);
  }, [
    eventContext,
    productClickedCorrelationId,
    dslConfigId,
    triggerFormEvents,
  ]);

  /**
   * Check for additional questions and either:
   * - Show the form if there are missing required fields
   * - Submit immediately if all required fields are present
   */
  const checkForAdditionalQuestionsAndSubmit = useCallback(async (): Promise<{
    hasQuestions: boolean;
  }> => {
    // Capture the open's conversion before the question lookup. That lookup
    // awaits the lead schema, and a newer selection opened during it replaces
    // the provider's promise. This submit still closes over the original
    // lead, so it must complete the conversion it started with.
    const resolveId = resolvePrecreatedConversionIdRef.current;
    const pendingClickConversion = resolveId?.();
    // Attach the rejection handler now. The question lookup awaits before
    // this promise is read, and a rejection during that gap must stay a
    // missing id rather than an unhandled failure.
    const capturedClickConversion = (
      pendingClickConversion ??
      Promise.resolve(precreatedConversionIdRef.current)
    ).catch(() => undefined);
    const requestResult = selectedResultRef.current;

    try {
      const questions = await fetchPostResultQuestions();

      if (questions.length === 0) {
        // No additional questions, submit immediately
        const submitResponse = await performLeadSubmission(
          inputs,
          capturedClickConversion,
        );
        // Fire event without PRQ data
        fireFormSubmittedEvent(productClickedCorrelationId, submitResponse);
        return { hasQuestions: false };
      } else {
        // Additional questions exist: the PII step is complete and the user
        // advances into the PRQ step. Fire FormContinued (DSL path) as the
        // Additional Questions form opens, then show it — DO NOT submit yet.
        // A newer program selected during the lookup keeps its own questions.
        // Still report hasQuestions so the caller does not record a submit
        // that never happened.
        if (selectedResultRef.current !== requestResult) {
          return { hasQuestions: true };
        }
        fireFormContinuedEvent();
        setAdditionalQuestions(questions);
        setShowAdditionalQuestions(true);
        return { hasQuestions: true };
      }
    } catch (error) {
      console.error('Error checking for additional questions:', error);
      if (selectedResultRef.current === requestResult) {
        setQuestionsError(
          error instanceof Error ? error : new Error('Unknown error'),
        );
      }
      throw error;
    }
  }, [
    lead,
    programId,
    productClickedCorrelationId,
    contactId,
    inputs,
    record,
    fireFormSubmittedEvent,
    fireFormContinuedEvent,
  ]);

  /**
   * Submit the lead with additional question answers
   */
  const submitWithAdditionalData = useCallback(
    async (additionalData: any) => {
      const requestResult = selectedResultRef.current;
      // Transform additionalData: map from question IDs to leadFieldAssociated
      const transformedData: Record<string, any> = {};

      Object.entries(additionalData).forEach(([questionId, answer]) => {
        const question = additionalQuestions.find((q) => q.id === questionId);
        const resolvedField =
          (question?.leadFieldAssociated as string) ||
          (question?.options?.find(
            (o) => o.leadFieldAssociated && String(o.value) === String(answer),
          )?.leadFieldAssociated as string);

        if (resolvedField) {
          transformedData[resolvedField] = {
            key: resolvedField,
            value: answer,
          };
        }
      });

      // Merge transformed data with existing inputs
      const mergedInputs = {
        ...inputs,
        ...transformedData,
      };

      // Submit lead with merged inputs - submitLead will rebuild the lead object from these inputs
      const submitResponse = await performLeadSubmission(mergedInputs);

      // Fire event with the raw PRQ form data
      fireFormSubmittedEvent(
        productClickedCorrelationId,
        submitResponse,
        additionalData,
      );

      // Reset state after successful submission. A newer program that
      // loaded its own questions while this submit was in flight keeps them.
      if (selectedResultRef.current === requestResult) {
        setShowAdditionalQuestions(false);
        setAdditionalQuestions([]);
      }
    },
    [
      inputs,
      record,
      productClickedCorrelationId,
      contactId,
      additionalQuestions,
      fireFormSubmittedEvent,
    ],
  );

  /**
   * Reset the additional questions state (useful for canceling)
   */
  const resetAdditionalQuestions = useCallback(() => {
    setShowAdditionalQuestions(false);
    setAdditionalQuestions([]);
  }, []);

  return {
    isSubmitting,
    showAdditionalQuestions,
    additionalQuestions,
    isLoadingQuestions,
    questionsError,
    checkForAdditionalQuestionsAndSubmit,
    submitWithAdditionalData,
    resetAdditionalQuestions,
  };
};

export default useLeadSubmissionWithQuestions;
